import reportMonitoringSDK from '@/assets/js/shared/services/report-monitoring-sdk';
import bugsnag from '@bugsnag/js';

const noRecordsFoundMessages = [
  'No records found for that Account ID.',
  'No record with that ID found.'
];

/**
 * Check if the response has no monitored records for the account.
 * 
 * @example
 * hasNoMonitoredRecords({ type: 'error-report-monitoring-database', msg: 'No records found for that Account ID.' })
 * // => true
 
 * @param {object} response - The response from the endpoint
 * @returns {boolean} - True if no monitored records found, false otherwise
 */
const hasNoMonitoredRecords = response => {
  return (
    response?.type === 'error-report-monitoring-database' &&
    noRecordsFoundMessages.includes(response?.msg)
  );
};

export const state = () => ({
  reportChangesCount: {
    reportsMonitored: 0,
    reportsChanged: 0,
    totalChanges: 0,
    reportsUnchanged: 0,
    needsReview: 0
  },
  latestMonitoringUpdates: [],
  hasError: false,
  limit: 20
});

export const mutations = {
  setReportChangesCount(state, changesCount) {
    state.reportChangesCount = changesCount;
  },
  setLatestMonitoringUpdates(state, latestMonitoringUpdates) {
    state.latestMonitoringUpdates = latestMonitoringUpdates;
  },
  setHasError(state, hasError) {
    state.hasError = hasError;
  }
};

export const actions = {
  async fetchReportChangesCount({ commit, rootState }) {
    let accountId;
    const initialState = {
      reportsMonitored: 0,
      reportsChanged: 0,
      totalChanges: 0,
      reportsUnchanged: 0,
      needsReview: 0
    };

    try {
      accountId = rootState.auth?.currentUser?.id;

      // If no account id, return early as calls below require it
      if (!accountId) {
        commit('setReportChangesCount', initialState);
        return;
      }

      const response = await reportMonitoringSDK().ReportMonitor.countChanges(
        accountId
      );

      // Prevent error from being reported as this is expected behavior
      if (hasNoMonitoredRecords(response)) {
        commit('setReportChangesCount', initialState);
        return;
      }

      // Any other error should be reported
      if (response.type && response.type.includes('error')) {
        throw new Error(response.msg);
      }

      commit('setReportChangesCount', response.data);
    } catch (error) {
      bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          page: 'store/report-monitor.js',
          action: 'fetchReportChangesCount',
          sdk: 'reportMonitoringSDK',
          sdkMethod: 'ReportMonitor.countChanges',
          endpoint: '/report-monitoring/v1/report-monitors/counts/account',
          parameters: {
            accountId
          }
        });
      });
      console.error(error);
      commit('setReportChangesCount', initialState);
    }
  },
  async fetchLatestMonitoringUpdates({ commit, rootState }) {
    let accountId;
    try {
      accountId = rootState.auth?.currentUser?.id;

      // If no account id, return early as calls below require it
      if (!accountId) {
        commit('setLatestMonitoringUpdates', []);
        return;
      }

      // Fetch the 3 latest monitoring updates
      const response = await reportMonitoringSDK().ReportMonitor.byAccountId(
        accountId,
        3, // limit
        0, // skip
        true, // sortDesc
        'last_updated', // sort
        null, // deleted
        true, // reportsWithChanges - only return reports with updates available
        true // needsReview - only return monitored reports that haven't been viewed yet
      );

      // Prevent error from being reported as this is expected behavior
      if (hasNoMonitoredRecords(response)) {
        commit('setLatestMonitoringUpdates', []);
        return;
      }

      // Any other error should be reported
      if (response.type && response.type.includes('error')) {
        throw new Error(response.msg);
      }

      commit('setLatestMonitoringUpdates', response);
    } catch (error) {
      bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          page: 'store/report-monitor.js',
          action: 'fetchLatestMonitoringUpdates',
          sdk: 'reportMonitoringSDK',
          sdkMethod: 'ReportMonitor.byAccountId',
          endpoint: '/report-monitoring/v1/report-monitors/account',
          parameters: {
            accountId
          }
        });
      });
      console.error(error);
      commit('setHasError', true);
      commit('setLatestMonitoringUpdates', []);
    }
  }
};

export const getters = {
  reportsMonitored: state => state.reportChangesCount?.reportsMonitored,
  updatesAvailable: state => state.reportChangesCount?.needsReview,
  totalChanges: state => state.reportChangesCount?.totalChanges,
  latestMonitoringUpdates: state => state.latestMonitoringUpdates,
  hasError: state => state.hasError,
  limit: state => state.limit
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
